import { Box, Card, CardContent, Grid, Typography, Tabs, Tab, Button } from "@mui/material";
import React, { useState, useMemo, useCallback } from "react";
import ParameterSet from "./ParametreSet";
import img2 from "../styles/img/scope.png";
import HookupConfig from "./HookupConfig";
import { useLocation, useNavigate, useParams } from "react-router";
import { equipmentScopeConfig } from "./EquipmentScope";
import axiosBase from "../common/axiosBase";
import coreConfig from "../common/coreConfig";
import HookupDocsGrid from "./HookupDocsGrid";
import { useStateContext } from "@durlabh/dframework-ui";
import { logger } from "../App";
import CommonUiModel from "../common/commonUiModel";

const { apiConfig, colorMapper, equipmentIdMapper } = coreConfig;
const equipmentScopeIdIndex = 0;
const tabsMap = [
    {
        id: 0,
        label: "Connectors",
        route: "hookup",
        component: ({ readOnly, dynamicColumns, baseFilters, onAdd, navigateToForm, onCellClick }) => (
            <HookupConfig.Grid 
                readOnly={readOnly} 
                dynamicColumns={dynamicColumns} 
                baseFilters={baseFilters} 
                onAddOverride={onAdd} 
                onCellDoubleClickOverride={navigateToForm} 
                onCellClick={onCellClick} 
            />
        )
    },
    {
        id: 1,
        label: "Parameter Sets",
        route: "parameterset",
        component: ({ readOnly, dynamicColumns, baseFilters, onAdd, navigateToForm, onCellClick }) => {
            return <ParameterSet.Grid
                readOnly={readOnly}
                dynamicColumns={dynamicColumns}
                baseFilters={baseFilters}
                onAddOverride={onAdd}
                onCellDoubleClickOverride={navigateToForm}
                onCellClick={onCellClick}
            />;
        }
    },
    {
        id: 2,
        label: "Documents",
        route: "document",
        component: ({ readOnly, baseFilters, equipmentId, id }) => <HookupDocsGrid readOnly={readOnly} baseFilters={baseFilters} equipmentId={equipmentId} id={id} />
    }
];

const EquipmentScopeForm = ({ equipmentId , ...props}) => {
    // For Enspire 3000, remove the EquipmentScopeRegion column
    const modelConfig = useMemo(() => {
        const config = {
            ...equipmentScopeConfig,
            columns: [...equipmentScopeConfig.columns],
            navigateBack: false,
            reloadOnSave: false // overriding the Equipment Scope Config.
        };
        if (Number(equipmentId) === equipmentIdMapper.Enspire3000) {
            config.columns = config.columns.filter(column => column.field !== 'EquipmentScopeRegion');
        }
        return config;
    }, [equipmentId]);
    const equipmentScopeModel = useMemo(() => new CommonUiModel(modelConfig), [equipmentScopeConfig.columns]);
    const EquipmentScopeModelForm = equipmentScopeModel.Form;
    return <EquipmentScopeModelForm {...props}/>;
};

export default function ScopeDetails() {
    const [state, setState] = useState({
        equipment: {},
        scope: {},
        activeTab: 0
    });
    const { stateData = {} } = useStateContext();
    const { userDetails = {} } = stateData.getUserData || {};
    const { equipmentId } = useParams();
    let { id } = useParams();
    id = id.split('_')[equipmentScopeIdIndex];
    const { equipment, scope } = state;
    const equipmentsArray = [equipmentIdMapper.Enspire3000];
    const fetchData = async () => {
        try {
            const [equipmentResponse, scopeResponse] = await Promise.all([
                axiosBase({
                    method: apiConfig.reqMethod.GET,
                    api: apiConfig.endpoints.equipment + "/" + equipmentId
                }),
                axiosBase({
                    method: apiConfig.reqMethod.GET,
                    api: apiConfig.endpoints.equipmentScope + "/" + id
                })
            ]);
            setState((prevState) => ({
                ...prevState,
                equipment: equipmentResponse.data.data,
                scope: scopeResponse.data.data
            }));
        } catch (error) {
			logger.error({ error, message: "Error while fetching equipment and scope data" });
        }
    };
    React.useEffect(() => {
        fetchData();
    }, []);

    const navigate = useNavigate();
    const location = useLocation();
    const currentTab = useMemo(() => tabsMap[state.activeTab], [state.activeTab]);
    const handleTabChange = useCallback((event, newValue) => {
        setState(prevState => ({ ...prevState, activeTab: newValue }));
    }, []);

    const navigateToForm = useCallback(({ id }) => {
        const path = `${location.pathname}/${currentTab.route}/${id}`;
        navigate(path);
    }, [location, currentTab]);

    const onAdd = () => {
        navigateToForm({ id: 0 });
    };

    const onCellClick = useCallback(({ cellParams, event, details }) => {
        let action = null;
        if (cellParams.field === 'actions') {
            action = details?.action;
            if (!action) {
                const el = event.target.closest('button');
                if (el) {
                    action = el.dataset.action;
                }
            }
        }
        if (!action) return;
        if (action === 'Edit') {
            navigateToForm({ id: cellParams.id });
        }
        else return false;
    },[navigateToForm]);

    const readOnly = useMemo(() => {
        const equipmentList = userDetails.equipmentsList || [];
        return scope?.RevisionStatus !== coreConfig.draft || !equipmentList.includes(Number(equipmentId));
    }, [scope, equipmentId]);

    const baseFilters = useMemo(() =>[{
        field: "EquipmentScopeId",
        operator: "equals",
        value: Number(id || 0), // EquipmentScopeId
        type: "number"
    }], [id]);

    const dynamicColumns = useMemo(() => {
        let json = '[]';
        let columns = [];
        if (state.activeTab === 0 && typeof equipment.HookupConfigValuesValidations === "string") {
            json = equipment.HookupConfigValuesValidations;
        } else if (state.activeTab === 1 && typeof equipment.ParameterSetsValuesValidations === "string") {
            json = equipment.ParameterSetsValuesValidations;
        }
        try {
            columns = JSON.parse(json);
        } catch(err) {
            logger.error({ error: err, message: "Error parsing dynamic columns" });
            return [];
        }
        columns.forEach(item => {
            item.width = 200;
            item.sortable = false;
            item.exportable = true;
            item.filterable = false;
            item.gridLabel = item.field;
        });
        return columns;
    }, [state.activeTab, equipment.HookupConfigValuesValidations, equipment.ParameterSetsValuesValidations]);

    return (
        <Grid container >
            <Grid size={12} sx={{ marginBottom: '10px', marginTop: "10px" }}>
                <Box display="flex" alignItems="center" justifyContent='space-between' sx={{
                    backgroundColor: colorMapper[scope.RevisionStatus],
                    borderRadius: '5px',
                    padding: '10px',
                    color: 'text.secondary'
                }}>
                    <Typography variant="h5">Scope Details</Typography>
                    <Typography variant="h5" m="auto">Status: {scope.RevisionStatus}</Typography>
                    <Typography variant="h5" m="auto">Revision - {scope.Revision}</Typography>
                    <Box>
                        <Button variant="contained" onClick={() => navigate(-1)}>Back</Button>
                    </Box>
                </Box>
            </Grid>
            {equipmentsArray.includes(state.equipment.EquipmentId) && !state.scope.SoldIn && <Grid size={12} sx={{ marginBottom: '10px' }}>
                <Box className="warningBar">
                    <Typography>
                        {`The ‘Sold In’ value has not been selected for Scope Model ${state.scope.ScopeModel}. As a result, this will not be validated for the enspire 3000`}
                    </Typography>
                </Box>
            </Grid>}
            <Grid size={12}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                p: 2,
                                height: 'fit-content'
                            }}
                        >
                            <Box sx={{ mb: 4 }}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    sx={{
                                        padding: 2,
                                        mb: 2
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: '#1976d2',
                                            fontWeight: 600
                                        }}
                                    >
                                        Equipment
                                    </Typography>
                                </Box>

                                <Card
                                    sx={{
                                        backgroundColor: 'transparent',
                                        boxShadow: 'none'
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Grid container spacing={3}>
                                            <Grid
                                                size={{ xs: 12, sm:4 }}
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        width: { xs: 80, sm: 96 },
                                                        height: { xs: 80, sm: 96 },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#f5f5f5',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <img
                                                        src={`/${equipment.EquipmentTypeImage}`}
                                                        alt="Equipment"
                                                        style={{
                                                            maxHeight: '100%',
                                                            maxWidth: '100%',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 8 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Typography variant="body1" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        <Box component="span" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                                                            Equipment Model:
                                                        </Box>
                                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                                            {equipment.ModelName || "N/A"}
                                                        </Box>
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        <Box component="span" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                                                            Equipment Type:
                                                        </Box>
                                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                                            {equipment.EquipmentType || "N/A"}
                                                        </Box>
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        <Box component="span" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                                                            Equipment Family:
                                                        </Box>
                                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                                            {equipment.EquipmentFamily || "N/A"}
                                                        </Box>
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    sx={{
                                        padding: 2,
                                        mb: 2
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: '#1976d2',
                                            fontWeight: 600
                                        }}
                                    >
                                        Scope
                                    </Typography>
                                </Box>
                                <Card
                                    sx={{
                                        backgroundColor: 'transparent',
                                        boxShadow: 'none'
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Grid container spacing={3} >
                                            <Grid
                                                size={{ xs: 12, sm: 4 }}
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        width: { xs: 80, sm: 96 },
                                                        height: { xs: 80, sm: 96 },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#f5f5f5',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <img
                                                        src={img2}
                                                        alt="Scope"
                                                        style={{
                                                            maxHeight: '100%',
                                                            maxWidth: '100%',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid size={{xs: 12, sm: 8 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Typography variant="body1" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        <Box component="span" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                                                            Manufacturer:
                                                        </Box>
                                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                                            {scope.ManufacturerName || "N/A"}
                                                        </Box>
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        <Box component="span" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                                                            Scope Type:
                                                        </Box>
                                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                                            {scope.ScopeType || "N/A"}
                                                        </Box>
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        <Box component="span" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                                                            Scope Model:
                                                        </Box>
                                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                                            {scope.ScopeModel || "N/A"}
                                                        </Box>
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Box sx={{ backgroundColor: '#fff', height: { xs: 'auto', lg: '100%' } }}>
                            <EquipmentScopeForm
                                equipmentId={equipmentId}
                                readOnly={readOnly}
                                sx={{ boxShadow: 'none !important' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
            <Grid className="custom-tabs-container" size={{ xs: 12 }} sx={{ backgroundColor: '#FFF', marginTop: '20px' }}>
                <Tabs value={state.activeTab} onChange={handleTabChange} aria-label="connector-parameter-tabs">
                    {tabsMap.map((tab) => (
                        <Tab key={tab.id} label={tab.label} />
                    ))}
                </Tabs>
                <Box sx={{ padding: 2 }}>
                    {currentTab.component({
                        readOnly,
                        dynamicColumns,
                        baseFilters,
                        equipmentId,
                        id,
                        onAdd,
                        navigateToForm,
                        onCellClick
                    })}
                </Box>
            </Grid>
        </Grid >
    );
}